import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Trophy, 
  TrendingUp,
  MessageCircle,
  Play,
  CheckCircle,
  ChevronRight,
  Sparkles,
  Activity,
  Flame
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';

const todaysPlan = [
  { id: 1, name: 'Knee Flexion Stretches', duration: '10 min', completed: true, videoUrl: '#' },
  { id: 2, name: 'Quad Strengthening', duration: '15 min', completed: true, videoUrl: '#' },
  { id: 3, name: 'Balance Exercises', duration: '10 min', completed: false, videoUrl: '#' },
  { id: 4, name: 'Walking Practice', duration: '20 min', completed: false, videoUrl: '#' },
];

const upcomingSession = {
  therapist: 'Dr. Lisa Chen',
  avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100',
  date: 'Today',
  time: '3:30 PM',
  type: 'In-person',
  location: 'Physiomobile Clinic, Level 3'
};

const achievements = [
  { name: '7-Day Streak', icon: Flame, achieved: true },
  { name: '10 Sessions', icon: Trophy, achieved: true },
  { name: 'Pain Free Week', icon: Activity, achieved: false },
];

export default function PatientDashboard() {
  const navigate = useNavigate();
  const completedCount = todaysPlan.filter(e => e.completed).length;
  const totalCount = todaysPlan.length;
  const progressPercent = (completedCount / totalCount) * 100;

  return (
    <div className="space-y-6">
      {/* Progress Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary to-primary-glow p-6 text-primary-foreground"
      >
        <div className="relative z-10">
          <h2 className="text-xl font-display font-bold mb-1">Week 6 of Recovery</h2>
          <p className="text-primary-foreground/80 text-sm mb-4">You're making great progress! Keep it up.</p>
          
          <div className="flex items-center gap-4 mb-3">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Overall Recovery</span>
                <span className="font-semibold">68%</span>
              </div>
              <div className="h-2 bg-primary-foreground/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '68%' }}
                  transition={{ duration: 1, delay: 0.3 }}
                  className="h-full bg-primary-foreground rounded-full"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-4 text-sm">
            <div>
              <span className="text-primary-foreground/70">Sessions: </span>
              <span className="font-semibold">12/20</span>
            </div>
            <div>
              <span className="text-primary-foreground/70">Compliance: </span>
              <span className="font-semibold">82%</span>
            </div>
            <div>
              <span className="text-primary-foreground/70">Streak: </span>
              <span className="font-semibold">7 days 🔥</span>
            </div>
          </div>
        </div>
        
        {/* Decorative element */}
        <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-primary-foreground/10 rounded-full" />
        <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-primary-foreground/10 rounded-full" />
      </motion.div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Today's Plan */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div>
                <CardTitle className="text-base font-display">Today's Exercises</CardTitle>
                <CardDescription>{completedCount} of {totalCount} completed</CardDescription>
              </div>
              <Badge variant="outline" className="gap-1">
                <CheckCircle className="w-3 h-3" />
                {Math.round(progressPercent)}%
              </Badge>
            </CardHeader>
            <CardContent>
              <Progress value={progressPercent} className="h-2 mb-4" />
              
              <div className="space-y-3">
                {todaysPlan.map((exercise, idx) => (
                  <motion.div
                    key={exercise.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`flex items-center gap-4 p-3 rounded-xl border transition-colors ${
                      exercise.completed 
                        ? 'bg-success-light/50 border-success/20' 
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      exercise.completed 
                        ? 'bg-success text-success-foreground' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {exercise.completed ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <p className={`font-medium ${
                        exercise.completed ? 'text-muted-foreground line-through' : 'text-foreground'
                      }`}>
                        {exercise.name}
                      </p>
                      <p className="text-sm text-muted-foreground">{exercise.duration}</p>
                    </div>
                    
                    {!exercise.completed && (
                      <Button size="sm">
                        Start
                      </Button>
                    )}
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* AI Companion CTA */}
          <Card className="bg-accent border-accent">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-semibold text-foreground mb-1">Need Help?</h3>
                  <p className="text-sm text-muted-foreground">Ask your AI Companion for exercise tips, motivation, or any questions!</p>
                </div>
                <Button onClick={() => navigate('/patient/companion')}>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Ask AI
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Right Column */}
        <div className="space-y-6">
          {/* Upcoming Session */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-display flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Next Session
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={upcomingSession.avatar} />
                  <AvatarFallback>LC</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium text-foreground">{upcomingSession.therapist}</p>
                  <p className="text-sm text-muted-foreground">Physiotherapist</p>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{upcomingSession.date}, {upcomingSession.time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline">{upcomingSession.type}</Badge>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground mb-4">{upcomingSession.location}</p>
              
              <Button variant="outline" className="w-full">
                Reschedule
              </Button>
            </CardContent>
          </Card>
          
          {/* Achievements */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-display flex items-center gap-2">
                <Trophy className="w-4 h-4 text-warning" />
                Achievements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {achievements.map((achievement, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      achievement.achieved 
                        ? 'bg-warning-light' 
                        : 'bg-muted opacity-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      achievement.achieved 
                        ? 'bg-warning text-warning-foreground' 
                        : 'bg-muted-foreground/20 text-muted-foreground'
                    }`}>
                      <achievement.icon className="w-4 h-4" />
                    </div>
                    <span className={`text-sm font-medium ${
                      achievement.achieved ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {achievement.name}
                    </span>
                    {achievement.achieved && (
                      <CheckCircle className="w-4 h-4 text-success ml-auto" />
                    )}
                  </div>
                ))}
              </div>
              
              <Button variant="ghost" className="w-full mt-4 text-sm" onClick={() => navigate('/patient/progress')}>
                View all progress <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
          
          {/* Quick Stats */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-display font-bold text-foreground">3</p>
                  <p className="text-xs text-muted-foreground">Pain Score</p>
                </div>
                <div className="text-center p-3 bg-muted rounded-lg">
                  <p className="text-2xl font-display font-bold text-success">+12%</p>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
